import React, { useState, useEffect, useRef } from "react";
import PulseLoader from "@/components/PulseLoader";
import { useLocation } from "react-router-dom";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import authService from "../../../utils/authService";
import ScheduleDisplay from "../../../components/ScheduleDisplay";
import SubjectReorderPanel from "../../../components/SubjectReorderPanel";
import DayTypeInsertModal from "../../../components/DayTypeInsertModal";
import TestTypeSelectModal from "../../../components/TestTypeSelectModal";
import {
  ScheduleData,
  Selection,
  CourseData,
  DayTypeMap,
  TestKind,
  extractMatchingDays,
  LEGACY_SPECIAL_DAY_TOPICS,
  editScheduleData,
  extractLearningOnlySchedule,
  recalculateScheduleForHourChange,
  updateDurationInPlace,
  getLastDateOfPreviousSubject,
  updateScheduleWithCustomDate,
  adjustSubsequentSubjects,
  editScheduleDataWithDayTypes,
  updateCustomSubjectDatesFromSchedule,
  isHoliday,
  reorderScheduleBySubjectNames,
  extractSubjectNamesFromSchedule,
  extractSubjectNamesFromCourseData,
  isSubjectReorderLocked,
  filterCourseDataByIds,
  filterScheduleBySelectedNames,
  mergeSubjectSelectionOrder,
  datedSubjectOrderChanged,
  subjectNamesToIds,
  subjectIdsToNames,
  canUnselectSubject,
  resolveSubjectReleasedIds,
  renumberScheduleDays,
  closeScheduleGapsAfterSubject,
  preserveUndatedSubjectDates,
  applyLiveSessionSubtopicIds,
} from "../../../utils/scheduleUtils";
import {
  applyLcmMarkersFromCoursePlans,
  clearLcmMarkersFromSchedule,
  SubjectLivePlanInput,
} from "../../../utils/courseLivePlanDayTypes";

const EditDayWise: React.FC = () => {
  const [schedule, setSchedule] = useState<ScheduleData>({});
  const [isLoading, setIsLoading] = useState(false);
  // Separate from isLoading (page fetch); drives confirm-modal save spinner only
  const [isSaving, setIsSaving] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({});
  const [selectedDurations, setSelectedDurations] = useState<{ [key: string]: string }>({});
  const [fetchedJson, setFetchedJson] = useState<CourseData>({});
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const data = location.state as Selection;
  const [modalNewMessage, setModalNewMessage] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [currentDay, setCurrentDay] = useState('');
  const [selectedDayType, setSelectedDayType] = useState('');
  const [startDate, setStartDate] = useState<string>("");

  const [editingDateForSubject, setEditingDateForSubject] = useState<string | null>(null);
  const [customSubjectDates, setCustomSubjectDates] = useState<{ [key: string]: string }>({});
  const [maxDay, setMaxDay] = useState<number>(0);
  const [originalSchedule, setOriginalSchedule] = useState<ScheduleData>({});
  const isInitialDayTypeSetRef = useRef(false);
  const skipDayTypeRebuildRef = useRef(false);
  // User-defined subject order; editScheduleData uses fetchedJson key order on regenerate
  const subjectOrderRef = useRef<string[]>([]);
  const fullCourseRef = useRef<CourseData>({});
  const selectedNamesRef = useRef<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const livePlanInputsRef = useRef<SubjectLivePlanInput[]>([]);
  const [includeLcmFromPlan, setIncludeLcmFromPlan] = useState(false);

  // Re-apply order after hour/day-type regeneration (those rebuild from course data)
  const applySubjectOrder = (newSchedule: ScheduleData): ScheduleData => {
    const order = subjectOrderRef.current;
    const ordered = order.length > 0
      ? reorderScheduleBySubjectNames(newSchedule, order)
      : newSchedule;
    return applyLiveSessionSubtopicIds(ordered);
  };
  const extractOptions = [...LEGACY_SPECIAL_DAY_TOPICS];
  const [dayType, setDayType] = useState<DayTypeMap>({});
  const [showTestModal, setShowTestModal] = useState(false);
  const [currentSubjectForTest, setCurrentSubjectForTest] = useState('');
  
  // Helper function to check if a day is restricted
  const isDayRestricted = (dayString: string): boolean => {
    if (maxDay === 0) return false; // No restrictions if maxDay is 0
    
    const dayNumber = parseInt(dayString.split(' ')[1]);
    return dayNumber <= maxDay;
  };

  // Helper function to check if a subject's date change is restricted
  const isSubjectDateRestricted = (subjectName: string): boolean => {
    if (maxDay === 0) return false; // No restrictions if maxDay is 0
    
    const subjectDays = schedule[subjectName];
    if (!Array.isArray(subjectDays) || subjectDays.length === 0) return false;

    // Newly mapped / undated subjects can always set or change their start date
    if (subjectDays.every((day: any) => !day.date)) return false;
    const originalDays = originalSchedule[subjectName];
    if (!Array.isArray(originalDays) || originalDays.length === 0 || originalDays.every((d: any) => !d.date)) {
      return false;
    }
    
    // Check if any day of this subject is within the restricted range
    return subjectDays.some((day: any) => {
      const dayNumber = parseInt(day.day.split(' ')[1]);
      return dayNumber <= maxDay;
    });
  };

  // Merge new schedule with original schedule, preserving restricted days
  const mergeScheduleWithRestrictions = (newSchedule: ScheduleData): ScheduleData => {
    if (maxDay === 0 || Object.keys(originalSchedule).length === 0) {
      return newSchedule;
    }

    const merged: ScheduleData = {};

    // Use newSchedule key order (not originalSchedule) so subject reorder is kept
    Object.keys(newSchedule).forEach(subjectName => {
      const originalSubjectDays = originalSchedule[subjectName] || [];
      const newSubjectDays = newSchedule[subjectName] || [];

      if (newSubjectDays.length > 0) {
        merged[subjectName] = newSubjectDays.map((newDay: any) => {
          const dayNumber = parseInt(newDay.day.split(' ')[1]);

          if (dayNumber <= maxDay) {
            const originalDay = originalSubjectDays.find((day: any) => day.day === newDay.day);
            if (originalDay) {
              return originalDay;
            }
          }

          return newDay;
        });
      } else if (originalSubjectDays.length > 0) {
        merged[subjectName] = originalSubjectDays;
      }
    });

    return merged;
  };
  
  // Create selection object that updates with startDate
  const selection: Selection = {
    saturdayStudyDay: data.saturdayStudyDay,
    startDate: startDate || new Date().toISOString().split('T')[0],
    sundayHoliday: data.sundayHoliday,
    course_id: data.course_id,
    batch_id: data.batch_id,
    hoursPerDay: data.hoursPerDay,
    };


useEffect(() => {
    const fetchJson = async () => {
      const json = {
        course_id: selection.course_id,
      };
      
      try {
        setIsLoading(true);
        const response = await authService.post(
          "admin/get_all_data_of_course/",
          json
        );
        setFetchedJson(response.data.course_data);
        await fetchMaxDay();
        await loadAndProcessBatchSchedule(response.data.course_data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchJson();
  }, []);

  const fetchMaxDay = async () => {
    try {
      const response = await authService.get(
        `admin/batch/max_day/${selection.batch_id}/`
      );
      setMaxDay(response.data.max_day || 0);
    } catch (error) {
      console.error("Error fetching max day:", error);
      setMaxDay(0);
    }
  };

    const loadAndProcessBatchSchedule = async (courseData: any) => {      
      try {
        setIsLoading(true);
        const response = await authService.get(
          `admin/batch/get_batch_daywise_json/${selection.course_id}/${selection.batch_id}/`
        );
        const rawData = response.data.data || {};
        const releasedFromApi: string[] | undefined =
          response.data.subject_released ??
          (Array.isArray(rawData.subject_released) ? rawData.subject_released : undefined);
        const scheduleData: Record<string, any> = { ...rawData };
        if ('subject_released' in scheduleData) {
          delete scheduleData.subject_released;
        }

        // Subjects already stored on this batch (do NOT auto-map every course subject)
        const mappedNames = Object.keys(scheduleData).filter((key) => Array.isArray(scheduleData[key]));
        const selectedIds = resolveSubjectReleasedIds(releasedFromApi, courseData, mappedNames);
        const selectedNames = subjectIdsToNames(courseData, selectedIds);
        const selectedNameSet = new Set(selectedNames);

        // Keep only selected/mapped subjects in the working schedule
        Object.keys(scheduleData).forEach((key) => {
          if (!Array.isArray(scheduleData[key]) || !selectedNameSet.has(key)) {
            delete scheduleData[key];
          }
        });
        selectedNames.forEach((name) => {
          if (!scheduleData[name]) {
            scheduleData[name] = [];
          }
        });
        
        // Sort subjects by their first day number
        const sortedScheduleData: ScheduleData = {};
        const subjectsWithDays: Array<{ name: string; firstDay: number; days: any[] }> = [];
        const subjectsWithoutDays: Array<{ name: string; days: any[] }> = [];
        
        selectedNames.forEach(subjectName => {
          const subjectDays = scheduleData[subjectName];
          if (Array.isArray(subjectDays) && subjectDays.length > 0) {
            const firstDay = subjectDays[0].day.split(' ')[1];
            subjectsWithDays.push({
              name: subjectName,
              firstDay: parseInt(firstDay),
              days: subjectDays
            });
          } else {
            subjectsWithoutDays.push({ name: subjectName, days: subjectDays || [] });
          }
        });
        
        // Sort by first day number
        subjectsWithDays.sort((a, b) => a.firstDay - b.firstDay);
        
        // Add sorted subjects
        subjectsWithDays.forEach(({ name, days }) => {
          sortedScheduleData[name] = days;
        });
        
        // Add subjects with no days at the end (selected order)
        subjectsWithoutDays.forEach(({ name, days }) => {
          sortedScheduleData[name] = days;
        });
        
        // Helper function to calculate duration for a subtopic
        const calculateSubtopicDuration = (subtopic: any) => {
          const subtopicData = Array.isArray(subtopic?.data) ? subtopic.data : [];
          const dataDuration = subtopicData.reduce((acc: number, item: any) => acc + parseFloat(item.time), 0) / 60;
          const mcqDuration = (subtopic.mcq?.level1 || 0) * 1 + (subtopic.mcq?.level2 || 0) * 1.5 + (subtopic.mcq?.level3 || 0) * 2;
          const codingDuration = (subtopic.coding?.level1 || 0) * 10 + (subtopic.coding?.level2 || 0) * 20 + (subtopic.coding?.level3 || 0) * 30;
          return dataDuration + (mcqDuration + codingDuration) / 60;
        };
        
        // Helper function to add MCQ/Coding data to day schedule
        const addMcqAndCoding = (daySchedule: any, subtopic: any) => {
          if (subtopic.mcq) {
            daySchedule.mcq[subtopic.id] = {
              level1: subtopic.mcq?.level1 || 0,
              level2: subtopic.mcq?.level2 || 0,
              level3: subtopic.mcq?.level3 || 0,
            };
          }
          if (subtopic.coding) {
            daySchedule.coding[subtopic.id] = {
              level1: subtopic.coding?.level1 || 0,
              level2: subtopic.coding?.level2 || 0,
              level3: subtopic.coding?.level3 || 0,
            };
          }
          if (subtopic.optionalMcq) {
            if (!daySchedule.optionalMcq) daySchedule.optionalMcq = {};
            daySchedule.optionalMcq[subtopic.id] = {
              level1: subtopic.optionalMcq?.level1 || 0,
              level2: subtopic.optionalMcq?.level2 || 0,
              level3: subtopic.optionalMcq?.level3 || 0,
            };
          }
          if (subtopic.optionalCoding) {
            if (!daySchedule.optionalCoding) daySchedule.optionalCoding = {};
            daySchedule.optionalCoding[subtopic.id] = {
              level1: subtopic.optionalCoding?.level1 || 0,
              level2: subtopic.optionalCoding?.level2 || 0,
              level3: subtopic.optionalCoding?.level3 || 0,
            };
          }
        };
        
        // For subjects with no days, generate days from course data starting after restricted days
        const generateDaysForNewSubject = (subjectName: string, subjectData: any) => {
          if (!subjectData || !subjectData.topics) return [];
          
          // Find the last day in the schedule
          let lastDay = maxDay; // Start after restricted days
          let lastDate = new Date(selection.startDate);
          
          // Find the actual last day by checking all subjects
          Object.keys(sortedScheduleData).forEach(sub => {
            const days = sortedScheduleData[sub];
            if (Array.isArray(days) && days.length > 0) {
              const sortedDays = [...days].sort((a: any, b: any) => 
                parseInt(a.day.split(' ')[1]) - parseInt(b.day.split(' ')[1])
              );
              const lastDayInSubject = sortedDays[sortedDays.length - 1];
              const dayNum = parseInt(lastDayInSubject.day.split(' ')[1]);
              if (dayNum > lastDay) {
                lastDay = dayNum;
                lastDate = new Date(lastDayInSubject.date);
              }
            }
          });
          
          // Start from day after maxDay or last day
          let currentDay = Math.max(lastDay, maxDay);
          let currentDate = new Date(lastDate);
          
          const generateDays = () => {
            const hoursPerDay = parseInt(selection.hoursPerDay || '1', 10);
            const days: any[] = [];
            let actualTime = 0;
            let daySchedule: any = null;
            let isFirstSubtopic = true;
            const topics = subjectData.topics || [];
            
            topics.forEach((topic: any) => {
              const subtopics = topic.subtopics || [];
              subtopics.forEach((subtopic: any) => {
                if (daySchedule === null || isHoliday(currentDate, selection.saturdayStudyDay, selection.sundayHoliday)) {
                  // Advance date until we reach a non-holiday
                  do {
                    currentDate.setDate(currentDate.getDate() + 1);
                  } while (isHoliday(currentDate, selection.saturdayStudyDay, selection.sundayHoliday));
                  
                  // Increment currentDay to get the next day number
                  currentDay++;
                  daySchedule = {
                    day: `Day ${currentDay}`,
                    topic: "",
                    date: currentDate.toISOString().split('T')[0],
                    dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
                    duration: hoursPerDay.toString(),
                    actualTime: "0.00",
                    mcq: {},
                    coding: {},
                    optionalMcq: {},
                    optionalCoding: {},
                    subtopicids: [],
                    content: {},
                  };
                  actualTime = 0;
                }
                
                const totalDuration = calculateSubtopicDuration(subtopic);
                const availableTime = hoursPerDay;
                
                if (isFirstSubtopic || (actualTime + totalDuration <= availableTime)) {
                  daySchedule.topic += (daySchedule.topic ? ", " : "") + `${subtopic.subtopicName}`;
                  daySchedule.subtopicids.push({ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName });
                  daySchedule.content[subtopic.id] = subtopic.data;
                  actualTime += totalDuration;
                  addMcqAndCoding(daySchedule, subtopic);
                  isFirstSubtopic = false;
                } else {
                  // Update actualTime before pushing the previous day
                  daySchedule.actualTime = actualTime.toFixed(2);
                  days.push({ ...daySchedule });
                  actualTime = totalDuration;
                  
                  // Move to next day - increment day number and advance date
                  currentDay++;
                  do {
                    currentDate.setDate(currentDate.getDate() + 1);
                  } while (isHoliday(currentDate, selection.saturdayStudyDay, selection.sundayHoliday));
                  
                  // Create new day for this subtopic
                  daySchedule = {
                    day: `Day ${currentDay}`,
                    topic: `${subtopic.subtopicName}`,
                    date: currentDate.toISOString().split('T')[0],
                    dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
                    duration: hoursPerDay.toString(),
                    actualTime: totalDuration.toFixed(2),
                    mcq: {},
                    coding: {},
                    optionalMcq: {},
                    optionalCoding: {},
                    subtopicids: [{ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName }],
                    content: { [subtopic.id]: subtopic.data },
                  };
                  addMcqAndCoding(daySchedule, subtopic);
                  isFirstSubtopic = true;
                }
              });
            });
            
            if (daySchedule) {
              daySchedule.actualTime = actualTime.toFixed(2);
              days.push(daySchedule);
            }
            return days;
          };
          
          return generateDays();
        };
        // Create a lookup map for course data by subject_name to avoid repeated iterations
        const courseDataMap: { [key: string]: any } = {};
        Object.values(courseData).forEach((subj: any) => {
          if (subj.subject_name) {
            courseDataMap[subj.subject_name] = subj;
          }
        });
        
        // Check each subject and add missing days from course JSON
        Object.keys(sortedScheduleData).forEach(subjectName => {
          const subjectData = courseDataMap[subjectName];
          
          if (!subjectData || !subjectData.topics) return;
          
          const existingDays = sortedScheduleData[subjectName];
          
          if (existingDays.length === 0) {
            // Selected but not yet dated — generate content only; user sets start date
            const generatedDays = generateDaysForNewSubject(subjectName, subjectData);
            if (generatedDays.length > 0) {
              sortedScheduleData[subjectName] = generatedDays.map((day: any) => ({
                ...day,
                date: '',
                dayOfWeek: '',
              }));
            }
          } else {
            // Check if course JSON has more content than what's in batch
            // Get all subtopics from course JSON
            const allSubtopics: any[] = [];
            subjectData.topics.forEach((topic: any) => {
              if (topic.subtopics) {
                allSubtopics.push(...topic.subtopics);
              }
            });
            
            // Get subtopics already in batch schedule for this subject
            const existingSubtopicIds = new Set();
            existingDays.forEach((day: any) => {
              if (day.subtopicids) {
                day.subtopicids.forEach((st: any) => {
                  existingSubtopicIds.add(st.subtopic_id);
                });
              }
            });
            
            // Check if there are subtopics in course JSON not in batch
            const hasMissingSubtopics = allSubtopics.some((st: any) => !existingSubtopicIds.has(st.id));
            
            if (hasMissingSubtopics) {
              // Filter out subtopics that already exist in batch
              const missingSubtopics = allSubtopics.filter(st => !existingSubtopicIds.has(st.id));
              
              // Generate additional days starting after the last day in batch
              const lastDayInSubject = existingDays.reduce((max: any, day: any) => {
                const dayNum = parseInt(day.day.split(' ')[1]);
                const maxNum = parseInt(max.day.split(' ')[1]);
                return dayNum > maxNum ? day : max;
              });
              
              const lastDayNum = parseInt(lastDayInSubject.day.split(' ')[1]);
              const lastDate = new Date(lastDayInSubject.date);
              
              // Start generating from day after maxDay or last day (whichever is later)
              let currentDay = Math.max(lastDayNum, maxDay);
              let currentDate = new Date(lastDate);
              
              // Generate days for missing subtopics only
              const hoursPerDay = parseInt(selection.hoursPerDay || '1', 10);
              const additionalDays: any[] = [];
              let actualTime = 0;
              let daySchedule: any = null;
              let isFirstSubtopic = true;
              
              missingSubtopics.forEach((subtopic: any) => {
                
                if (daySchedule === null || isHoliday(currentDate, selection.saturdayStudyDay, selection.sundayHoliday)) {
                  // Advance date until we reach a non-holiday
                  do {
                    currentDate.setDate(currentDate.getDate() + 1);
                  } while (isHoliday(currentDate, selection.saturdayStudyDay, selection.sundayHoliday));
                  
                  // Only increment when creating a new day
                  currentDay++;
                  
                  daySchedule = {
                    day: `Day ${currentDay}`,
                    topic: "",
                    date: currentDate.toISOString().split('T')[0],
                    dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
                    duration: hoursPerDay.toString(),
                    actualTime: "0.00",
                    mcq: {},
                    coding: {},
                    optionalMcq: {},
                    optionalCoding: {},
                    subtopicids: [],
                    content: {},
                  };
                  actualTime = 0;
                }
                
                const totalDuration = calculateSubtopicDuration(subtopic);
                const availableTime = hoursPerDay;
                
                if (isFirstSubtopic || (actualTime + totalDuration <= availableTime)) {
                  daySchedule.topic += (daySchedule.topic ? ", " : "") + `${subtopic.subtopicName}`;
                  daySchedule.subtopicids.push({ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName });
                  daySchedule.content[subtopic.id] = subtopic.data;
                  actualTime += totalDuration;
                  addMcqAndCoding(daySchedule, subtopic);
                  isFirstSubtopic = false;
                } else {
                  daySchedule.actualTime = actualTime.toFixed(2);
                  additionalDays.push({ ...daySchedule });
                  actualTime = totalDuration;
                  
                  currentDay++;
                  do {
                    currentDate.setDate(currentDate.getDate() + 1);
                  } while (isHoliday(currentDate, selection.saturdayStudyDay, selection.sundayHoliday));
                  
                  daySchedule = {
                    day: `Day ${currentDay}`,
                    topic: `${subtopic.subtopicName}`,
                    date: currentDate.toISOString().split('T')[0],
                    dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
                    duration: hoursPerDay.toString(),
                    actualTime: totalDuration.toFixed(2),
                    mcq: {},
                    coding: {},
                    optionalMcq: {},
                    optionalCoding: {},
                    subtopicids: [{ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName }],
                    content: { [subtopic.id]: subtopic.data },
                  };
                  addMcqAndCoding(daySchedule, subtopic);
                  isFirstSubtopic = true;
                }
              });
              
              if (daySchedule) {
                daySchedule.actualTime = actualTime.toFixed(2);
                additionalDays.push(daySchedule);
              }
              
              if (additionalDays.length > 0) {
                sortedScheduleData[subjectName] = [...existingDays, ...additionalDays];
              }
            }
          }
        });
        
        setSchedule(applyLiveSessionSubtopicIds(sortedScheduleData));
        // Store the original schedule to preserve restricted days
        setOriginalSchedule(JSON.parse(JSON.stringify(sortedScheduleData)));

        const loadedOrder = Object.keys(sortedScheduleData);
        subjectOrderRef.current = loadedOrder;
        fullCourseRef.current = courseData;
        // Working course data = selected only, in calendar (first-day) order — not subject_released/API key order
        setFetchedJson(filterCourseDataByIds(courseData, subjectNamesToIds(courseData, loadedOrder)));
        selectedNamesRef.current = loadedOrder;
        setSelectedNames(loadedOrder);

        const hasExistingLcm = Object.values(sortedScheduleData).some(
          (days) =>
            Array.isArray(days) && days.some((day) => day?.live_session_lcm),
        );
        setIncludeLcmFromPlan(hasExistingLcm);

        try {
          const planInputs: SubjectLivePlanInput[] = [];
          await Promise.all(
            Object.entries(courseData).map(async ([subjectId, subjectData]) => {
              const subject = subjectData as { subject_name?: string; topics?: SubjectLivePlanInput["courseTopics"] };
              const subjectName = subject?.subject_name || subjectId;
              if (!loadedOrder.includes(subjectName)) return;
              try {
                const planRes = await authService.get(
                  `admin/course_live_session_plan/${selection.course_id}/${subjectId}/`,
                );
                const sessions = planRes?.data?.sessions || [];
                if (!sessions.length) return;
                planInputs.push({
                  subjectName,
                  sessions,
                  courseTopics: subject?.topics || [],
                });
              } catch (planErr) {
                console.warn(
                  `No course live session plan for ${subjectId}:`,
                  planErr,
                );
              }
            }),
          );
          livePlanInputsRef.current = planInputs;
        } catch (planErr) {
          console.warn("Failed to load course live session plans:", planErr);
        }

        const finalSchedule: ScheduleData = sortedScheduleData;
        
        const subjectStartDates: { [key: string]: string } = {};
        let earliestDate: string | null = null;
        
        Object.keys(finalSchedule).forEach(subjectName => {
          const subjectDays = finalSchedule[subjectName];
          if (Array.isArray(subjectDays) && subjectDays.length > 0) {
            const datedDays = subjectDays.filter((d: any) => d.date);
            if (datedDays.length === 0) return;

            const sortedDays = [...datedDays].sort((a: any, b: any) => 
              new Date(a.date).getTime() - new Date(b.date).getTime()
            );
            const firstDate = sortedDays[0].date as string;
            subjectStartDates[subjectName] = firstDate;
            
            if (!earliestDate || new Date(firstDate) < new Date(earliestDate)) {
              earliestDate = firstDate;
            }
          }
        });
        
        // Set the startDate to the earliest date found
        if (earliestDate) {
          selection.startDate = earliestDate;
          setStartDate(earliestDate);
        }
        
        setCustomSubjectDates(subjectStartDates);
        
        const days = extractMatchingDays(finalSchedule, extractOptions);        
        isInitialDayTypeSetRef.current = false;
        setDayType(days);

        // Use a longer timeout to ensure useEffect has already executed and returned early
         setTimeout(() => {
          isInitialDayTypeSetRef.current = true;
        }, 200);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    };


  const setSelectedOption = (value: string, day: string) => {
    setSelectedOptions(prevState => ({
      ...prevState,
      [day]: value,
    }));
  };
  
   useEffect(()=>{
    if (!isInitialDayTypeSetRef.current) {
      return;
    }
    
    if (!fetchedJson || Object.keys(fetchedJson).length === 0) {
      return;
    }
    
    // Debounce dayType changes to avoid excessive recalculations
    const timeoutId = setTimeout(() => {
      // Keep specials already on the schedule, then apply dayType updates
      const mergedDayType = {
        ...extractMatchingDays(schedule, extractOptions),
        ...dayType,
      };
      const baseSchedule = extractLearningOnlySchedule(schedule);
      const finalSchedule = editScheduleDataWithDayTypes(
        baseSchedule,
        mergedDayType,
        customSubjectDates,
        selection.startDate,
        selection.saturdayStudyDay,
        selection.sundayHoliday
      );
      const mergedSchedule = applySubjectOrder(
        preserveUndatedSubjectDates(
          schedule,
          mergeScheduleWithRestrictions(finalSchedule)
        )
      );
      setSchedule(mergedSchedule);
      setCustomSubjectDates(updateCustomSubjectDatesFromSchedule(mergedSchedule));
    }, 100);
    
    return () => clearTimeout(timeoutId);
   },[dayType]) 

  const setSelectedDuration = (value: string, day: string) => {
    setSelectedDurations(prevState => ({
      ...prevState,
      [day]: value,
    }));
  };

  const handleTimeClick = (selectedDay: string, hours: string) => {
    if (isDayRestricted(selectedDay)) {
      alert(`Cannot modify hours for Day ${selectedDay.split(' ')[1]} as it is within the restricted range (up to Day ${maxDay})`);
      return;
    }
    // Recalculate content + actualTime using existing schedule (does NOT rebuild from course JSON).
    // This preserves other days because we repack from what's already in `schedule`.
    const finalSchedule = recalculateScheduleForHourChange(
      schedule,
      fetchedJson,
      selection,
      selectedDay,
      Number(hours),
      customSubjectDates
    );

    const mergedSchedule = applySubjectOrder(
      preserveUndatedSubjectDates(
        schedule,
        mergeScheduleWithRestrictions(finalSchedule)
      )
    );

    // Keep custom subject start dates in sync with the new packed order
    setCustomSubjectDates(updateCustomSubjectDatesFromSchedule(mergedSchedule));
    setSchedule(mergedSchedule);
    // Clear stale dropdown selections after repacking/renumbering.
    setSelectedDurations({});

    // Sync dayType keys with the new Day N numbering, but prevent the dayType effect
    // from immediately rebuilding again.
    skipDayTypeRebuildRef.current = true;
    setDayType(extractMatchingDays(mergedSchedule, extractOptions));
  };



  const handleGoClick = (selectedDay: string, selectedOption: string, subjectName: string) => {
    if (isDayRestricted(selectedDay)) {
      alert(`Cannot add day type for Day ${selectedDay.split(' ')[1]} as it is within the restricted range (up to Day ${maxDay})`);
      return;
    }
    if (!selectedOption) return;

    setCurrentDay(selectedDay);
    setCurrentSubjectForTest(subjectName);

    if (selectedOption === 'Test') {
      setShowTestModal(true);
      return;
    }

    setSelectedDayType(selectedOption);
    setShowInsertModal(true);
  };

  const handleTestModalConfirm = (testTypes: TestKind[], insertToCurrentDay: boolean) => {
    setShowTestModal(false);
    const dayToUpdate = insertToCurrentDay
      ? currentDay
      : `Day ${parseInt(currentDay.split(' ')[1], 10) + 1}`;

    setDayType((prevState) => ({
      ...prevState,
      [dayToUpdate]: { kind: 'test', testTypes },
    }));
    setSelectedOption('', currentDay);
  };

 const handleInsertModalConfirm = (insertToCurrentDay: boolean) => {
    setShowInsertModal(false);
    
    const dayToUpdate = insertToCurrentDay 
      ? currentDay 
      : `Day ${parseInt(currentDay.split(' ')[1]) + 1}`;
      
          setDayType(prevState => ({
      ...prevState,
      [dayToUpdate]: selectedDayType,
    }));
    
    setSelectedOption("", currentDay);
  };

  const handleDateEditClick = (subjectName: string) => {
    // Check if the subject's date change is restricted
    if (isSubjectDateRestricted(subjectName)) {
      alert(`Cannot modify dates for subject "${subjectName}" as it has days within the restricted range (up to Day ${maxDay})`);
      return;
    }

    setEditingDateForSubject(subjectName);
  };

  const handleDateChange = (subjectName: string, newDate: string) => {
    if (!newDate) return;

    const previousSubjectLastDate = getLastDateOfPreviousSubject(schedule, subjectName, selection.startDate);
    const newDateObj = new Date(newDate);
    const previousDate = new Date(previousSubjectLastDate);

    // Allow going back to the original date or any date after the previous subject
    if (previousSubjectLastDate && newDateObj < previousDate) {
      alert(`Date must be on or after ${previousDate.toLocaleDateString()}. Please select a date from ${previousDate.toLocaleDateString()} onwards`);
      return;
    }

    setCustomSubjectDates(prev => ({
      ...prev,
      [subjectName]: newDate
    }));

    // Fill all days of this subject from the chosen start date
    let updated = updateScheduleWithCustomDate(schedule, subjectName, newDate, selection.saturdayStudyDay, selection.sundayHoliday);
    // Push later subjects forward only if they overlap — do not snap this subject's date back
    updated = adjustSubsequentSubjects(updated, subjectName, selection.saturdayStudyDay, selection.sundayHoliday);

    // Merge with restrictions to preserve restricted days
    updated = mergeScheduleWithRestrictions(updated);
    
    const newCustomDates = updateCustomSubjectDatesFromSchedule(updated);
    setCustomSubjectDates(newCustomDates);
    
    // Date edits don't rebuild from fetchedJson; still re-apply order for consistency
    setSchedule(applySubjectOrder(updated));
    setEditingDateForSubject(null);
  };

  const handleDateEditCancel = () => {
    setEditingDateForSubject(null);
  };

  const handleSubjectReorder = (newOrder: string[]) => {
    subjectOrderRef.current = newOrder;
    selectedNamesRef.current = newOrder;
    setSelectedNames(newOrder);
    const course = Object.keys(fullCourseRef.current).length ? fullCourseRef.current : fetchedJson;
    // Keep fetchedJson in sync so hour/day-type regenerate uses the new order
    const reorderedCourseData = filterCourseDataByIds(course, subjectNamesToIds(course, newOrder));
    setFetchedJson(reorderedCourseData);

    let reordered = reorderScheduleBySubjectNames(schedule, newOrder);

    // Drop any subjects no longer selected
    reordered = filterScheduleBySelectedNames(reordered, newOrder);

    const previousOrder = Object.keys(schedule);
    // Only rewrite dates when dated subjects move or a dated subject is removed.
    // Checking/unchecking an undated subject must leave the existing calendar alone.
    const shouldRealignDates = datedSubjectOrderChanged(previousOrder, newOrder, schedule);

    if (shouldRealignDates) {
      let anchorIndex = -1;
      // Last locked subject is the anchor; only reorder dates for subjects after it
      for (let i = newOrder.length - 1; i >= 0; i--) {
        if (isSubjectReorderLocked(schedule, newOrder[i], new Date(), maxDay)) {
          anchorIndex = i;
          break;
        }
      }

      if (anchorIndex >= 0 && anchorIndex < newOrder.length - 1) {
        reordered = adjustSubsequentSubjects(
          reordered,
          newOrder[anchorIndex],
          selection.saturdayStudyDay,
          selection.sundayHoliday
        );
      } else if (anchorIndex === -1 && newOrder.length > 1) {
        reordered = adjustSubsequentSubjects(
          reordered,
          newOrder[0],
          selection.saturdayStudyDay,
          selection.sundayHoliday
        );
      }
    }

    // Newly selected subjects: generate content/day structure but leave dates empty
    // (user sets start date via the date picker — auto dates hit maxDay/past restrictions)
    const missing = newOrder.filter((name) => !reordered[name]?.length);
    if (missing.length > 0) {
      const generated = editScheduleData(
        filterCourseDataByIds(course, subjectNamesToIds(course, missing)),
        selection,
        [parseInt(selection.hoursPerDay || '4', 10)]
      );
      missing.forEach((name) => {
        if (!generated[name]) return;
        reordered[name] = generated[name].map((day) => ({
          ...day,
          date: '',
          dayOfWeek: '',
        }));
      });
      reordered = reorderScheduleBySubjectNames(reordered, newOrder);
    }

    // Renumber / close gaps only when dated subjects changed. Otherwise only
    // renumber so newly generated undated days don't reuse Day 1..N.
    if (shouldRealignDates && newOrder.length > 0) {
      reordered = closeScheduleGapsAfterSubject(
        reordered,
        newOrder[0],
        selection.saturdayStudyDay,
        selection.sundayHoliday,
        maxDay
      );
    } else {
      reordered = renumberScheduleDays(reordered, maxDay);
    }

    reordered = mergeScheduleWithRestrictions(reordered);
    setCustomSubjectDates(updateCustomSubjectDatesFromSchedule(reordered));
    setSchedule(applySubjectOrder(reordered));
  };

  const handleSelectionChange = (newSelected: string[]) => {
    const currentOrder =
      subjectOrderRef.current.length > 0
        ? subjectOrderRef.current
        : selectedNamesRef.current.length > 0
          ? selectedNamesRef.current
          : Object.keys(schedule);
    handleSubjectReorder(mergeSubjectSelectionOrder(currentOrder, newSelected));
  };

  const applyOrClearAllLcm = (
    baseSchedule: ScheduleData,
    include: boolean,
  ): ScheduleData => {
    if (!include) return clearLcmMarkersFromSchedule(baseSchedule);
    if (!livePlanInputsRef.current.length) return baseSchedule;
    return applyLcmMarkersFromCoursePlans(
      baseSchedule,
      livePlanInputsRef.current,
    );
  };

  const handleIncludeLcmToggle = (include: boolean) => {
    setIncludeLcmFromPlan(include);
    setSchedule((prev) =>
      applySubjectOrder(
        mergeScheduleWithRestrictions(applyOrClearAllLcm(prev, include)),
      ),
    );
  };

  const handleSave = async () => {
    const missingDates = (selectedNamesRef.current.length > 0
      ? selectedNamesRef.current
      : Object.keys(schedule)
    ).filter((name) => {
      const days = schedule[name];
      return !Array.isArray(days) || days.length === 0 || days.some((d) => !d.date);
    });

    if (missingDates.length > 0) {
      alert(
        `Set dates for all selected subjects before saving.\nMissing: ${missingDates.join(', ')}`
      );
      return;
    }

    if (selectedNamesRef.current.length === 0 && Object.keys(schedule).length === 0) {
      alert('Select at least one subject before saving.');
      return;
    }

    setShowModal(true);
  };

  const handleModalOk = async () => {
    setIsSaving(true);
    try {
      const course = Object.keys(fullCourseRef.current).length ? fullCourseRef.current : fetchedJson;
      const response = await authService.post('admin/batch/save_daywise/', {
        schedule: applyLiveSessionSubtopicIds(schedule),
        course_id: selection.course_id,
        batch_id: selection.batch_id,
        subject_released: subjectNamesToIds(course, selectedNamesRef.current),
      });
      setShowModal(false);
      if(response.status === 200) {
        setShowUpdateModal(true);
        setModalNewMessage('Schedule updated successfully!');
      }
    } catch (error) {
      console.error('Error sending data:', error);
      setShowModal(false);
      setShowUpdateModal(true);
      setModalNewMessage('Error updating schedule!');
    } finally {
      setIsSaving(false);
    }
  };

  const subjectNames = selectedNames.length > 0 ? selectedNames : extractSubjectNamesFromSchedule(schedule);
  const allSubjectNames = extractSubjectNamesFromCourseData(
    Object.keys(fullCourseRef.current).length ? fullCourseRef.current : fetchedJson
  ).map((s) => s.subjectName);

  const subjectsMissingDates = subjectNames.filter((name) => {
    const days = schedule[name];
    return !Array.isArray(days) || days.length === 0 || days.some((d) => !d.date);
  });
  const canSave = subjectNames.length > 0 && subjectsMissingDates.length === 0;

  return (
    <div className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
    style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }} >

      <div >

        <div className="container-fluid mt-2">
          <div className="d-flex justify-content-between align-items-center">
            <div className="flex-grow-1">
              {!canSave && subjectNames.length > 0 && (
                <div className="alert alert-info mb-0 mt-2" role="alert">
                  Set a start date for every selected subject before saving
                  {subjectsMissingDates.length > 0 ? `: ${subjectsMissingDates.join(', ')}` : ''}.
                </div>
              )}
            </div>
            <div className="ms-3 d-flex align-items-center gap-3">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="add-live-session"
                  checked={includeLcmFromPlan}
                  onChange={(e) => handleIncludeLcmToggle(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="add-live-session"
                  style={{ fontSize: 13 }}
                >
                  Add live session
                </label>
              </div>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving || !canSave}
                title={!canSave ? 'Set dates for all selected subjects before saving' : undefined}
              >
                Save
              </button>
            </div>
          </div>
          <div className="container-fluid mt-2">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <PulseLoader size={10} />
              </div>
            ) : allSubjectNames.length > 0 || Object.keys(schedule).length > 0 ? (
              <>
                <SubjectReorderPanel
                  mode="edit"
                  subjects={subjectNames}
                  allSubjects={allSubjectNames}
                  isSubjectLocked={(name) => isSubjectReorderLocked(schedule, name, new Date(), maxDay)}
                  canUnselect={(name) => canUnselectSubject(schedule, name, new Date(), maxDay)}
                  onReorder={handleSubjectReorder}
                  onSelectionChange={handleSelectionChange}
                />
                {Object.keys(schedule).length > 0 ? (
                <ScheduleDisplay
                schedule={schedule}
                selection={selection}
                selectedDurations={selectedDurations}
                selectedOptions={selectedOptions}
                editingDateForSubject={editingDateForSubject}
                customSubjectDates={customSubjectDates}
                maxDay={maxDay}
                isDayRestricted={isDayRestricted}
                isSubjectDateRestricted={isSubjectDateRestricted}
                onDurationChange={(day, hours) => {
                  setSelectedDuration(hours, day);
                  handleTimeClick(day, hours);
                }}
                onDayTypeChange={(day, option) => setSelectedOption(option, day)}
                onGoClick={(day, option, subjectName) => {
                  handleGoClick(day, option, subjectName);
                }}
                onDateEditClick={handleDateEditClick}
                onDateChange={handleDateChange}
                onDateEditCancel={handleDateEditCancel}
              />
                ) : (
                  <p className="text-muted mt-3">Select at least one subject to build the schedule.</p>
                )}
              </>
            ) : (
              <p>No schedule data available.</p>
            )}
          </div>
        </div>
      </div>
      <DayTypeInsertModal
          show={showInsertModal}
          onHide={() => setShowInsertModal(false)}
          onConfirm={handleInsertModalConfirm}
        />
      <TestTypeSelectModal
        show={showTestModal}
        onHide={() => setShowTestModal(false)}
        subjectIndex={extractSubjectNamesFromSchedule(schedule).indexOf(currentSubjectForTest)}
        onConfirm={handleTestModalConfirm}
      />
      <Modal show={showModal} onHide={() => !isSaving && setShowModal(false)} centered>
        <Modal.Header closeButton={!isSaving}>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to proceed? 
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleModalOk} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Yes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Message</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalNewMessage}</Modal.Body>
            </Modal>
    </div>
  );
}

export default EditDayWise;
